import React, { useEffect, useState } from 'react';
import { Divider, Skeleton, Table } from '@mantine/core';
import dayjs from 'dayjs';
import NumberFormat from 'react-number-format';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { formatLocalDate, formatUtcDate } from '../../../helpers/format';
import AppCard from '../../common/AppCard';
import AppStack from '../../common/AppStack';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import { getResponsiveStyle as rs } from '../../../helpers/styles';
import { useMediaQueries } from '../../../helpers/hooks';
import CheckoutRegCartLineItem from '../checkout/CheckoutRegCartLineItem';
import WsCheckoutLineItem from '../ecommerce/webstore/sections/checkoutContent/WsCheckoutLineItem';
import {
  PAYMENT_PROVIDER_LINE_ITEM_TYPES,
  WEBSITE_PAYMENT_STATUS_LIST
} from '../../../config/constants';
import WsAccountOrderSummaryProductsGrouped from '../ecommerce/webstore/sections/accountContent/WsAccountOrderSummaryProductsGrouped';
import { translateStoreOrderData } from '../../../helpers/storeHelper';
import WsAccountOrderProductTrackingModal from '../ecommerce/webstore/sections/accountContent/WsAccountOrderProductTrackingModal';

const OrderDetailsInfo = ({
  isLoading,
  regCart,
  withBorder,
  hideTitle,
  showPaymentPlan
}) => {
  const { search } = useLocation();
  const [modalState, setModalState] = useState({
    isOpen: false,
    pkEcomOrderProduct: null
  });
  const { isTabletOrSmaller, mqIndex } = useMediaQueries();
  const {
    ecomOrderProducts: productData,
    productDataGroupedByPackage: groupedProductData
  } = translateStoreOrderData(regCart?.ecomOrder);

  const isPendingPayment = regCart?.regCartProducts.some(
    (p) => p.regFormSubmission.status === 'PENDING_PAYMENT'
  );
  const hasBeenRefunded = regCart?.cart.lineItems.some(
    (i) => i.refundedAmountInCents > 0
  );
  const currency = regCart?.cart.currency?.toUpperCase();
  const checkoutCart = regCart?.cart;

  const baseUrl = regCart?.regAssociation
    ? `/association/${regCart.regAssociation.pkRegAssociation}`
    : null;

  const paymentPlans =
    regCart?.cart.payments?.filter((p) => p.installments.length > 0) ?? [];

  const searchParams = new URLSearchParams(search);
  const trackingProduct = searchParams.get('tracking');
  const selectedProduct = productData.find(
    (p) =>
      p.pkEcomOrderProduct.toString() ===
      modalState.pkEcomOrderProduct?.toString()
  );

  useEffect(() => {
    if (trackingProduct) {
      setModalState({
        isOpen: true,
        pkEcomOrderProduct: trackingProduct
      });
    }
  }, [trackingProduct]);

  return !isLoading && regCart ? (
    <>
      <WsAccountOrderProductTrackingModal
        baseStoreUrl={baseUrl}
        ecomOrder={regCart?.ecomOrder}
        ecomOrderProduct={selectedProduct}
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
      />

      <AppCard style={{ border: withBorder ? 'solid 1px lightgrey' : 'none' }}>
        <AppFlexbox
          style={{
            padding: '0px 15px',
            alignItems: 'start',
            flexDirection: rs(
              ['column-reverse', 'column-reverse', 'row'],
              mqIndex
            )
          }}
        >
          <AppStack
            style={{
              flex: 1,
              gap: 0,
              alignSelf: 'stretch'
            }}
          >
            {!hideTitle && (
              <AppText size="lg" weight={700}>
                Order Summary
              </AppText>
            )}

            <AppStack style={{ gap: 0 }}>
              <AppFlexbox
                style={{
                  justifyContent: 'space-between',
                  flexWrap: 'nowrap',
                  gap: 40
                }}
              >
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                  Subtotal
                </AppText>
                <AppFlexbox style={{ gap: 10 }}>
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    <NumberFormat
                      decimalScale={2}
                      displayType="text"
                      fixedDecimalScale
                      prefix="$"
                      thousandSeparator
                      value={checkoutCart.subtotalInCents / 100}
                    />
                  </AppText>
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    {currency}
                  </AppText>
                </AppFlexbox>
              </AppFlexbox>
              {checkoutCart.discountTotalInCents > 0 && (
                <AppFlexbox
                  style={{
                    justifyContent: 'space-between',
                    flexWrap: 'nowrap'
                  }}
                >
                  <AppText style={{ fontSize: 14, color: '#666' }}>
                    Discount
                  </AppText>
                  <AppFlexbox style={{ gap: 10 }}>
                    <AppText style={{ fontSize: 14, color: '#666' }}>
                      <NumberFormat
                        decimalScale={2}
                        displayType="text"
                        fixedDecimalScale
                        prefix="$"
                        thousandSeparator
                        value={(checkoutCart.discountTotalInCents / 100) * -1}
                      />
                    </AppText>
                    <AppText style={{ fontSize: 14, color: '#666' }}>
                      {currency}
                    </AppText>
                  </AppFlexbox>
                </AppFlexbox>
              )}
              {checkoutCart?.totalInCents > 0 &&
                checkoutCart.lineItems.some((i) => i.isPhysicalProduct) && (
                  <AppFlexbox
                    style={{
                      justifyContent: 'space-between',
                      flexWrap: 'nowrap'
                    }}
                  >
                    <AppText style={{ fontSize: 14, color: '#666' }}>
                      Shipping
                    </AppText>
                    <AppFlexbox style={{ gap: 10 }}>
                      <AppText style={{ fontSize: 14, color: '#666' }}>
                        <NumberFormat
                          decimalScale={2}
                          displayType="text"
                          fixedDecimalScale
                          prefix="$"
                          thousandSeparator
                          value={checkoutCart.shippingTotalInCents / 100}
                        />
                      </AppText>
                      <AppText style={{ fontSize: 14, color: '#666' }}>
                        {currency}
                      </AppText>
                    </AppFlexbox>
                  </AppFlexbox>
                )}

              <AppFlexbox
                style={{
                  justifyContent: 'space-between',
                  flexWrap: 'nowrap'
                }}
              >
                <AppText style={{ fontSize: 14, color: '#666' }}>Taxes</AppText>
                <AppFlexbox style={{ gap: 10 }}>
                  <AppText style={{ fontSize: 14, color: '#666' }}>
                    <NumberFormat
                      decimalScale={2}
                      displayType="text"
                      fixedDecimalScale
                      prefix="$"
                      thousandSeparator
                      value={checkoutCart.taxTotalInCents / 100}
                    />
                  </AppText>
                  <AppText style={{ fontSize: 14, color: '#666' }}>
                    {currency}
                  </AppText>
                </AppFlexbox>
              </AppFlexbox>

              {checkoutCart.customerServiceFeeTotalInCents > 0 && (
                <AppFlexbox
                  style={{
                    justifyContent: 'space-between',
                    flexWrap: 'nowrap'
                  }}
                >
                  <AppText style={{ fontSize: 14, color: '#666' }}>
                    Service Fee
                  </AppText>
                  <AppFlexbox style={{ gap: 10 }}>
                    <AppText style={{ fontSize: 14, color: '#666' }}>
                      <NumberFormat
                        decimalScale={2}
                        displayType="text"
                        fixedDecimalScale
                        prefix="$"
                        thousandSeparator
                        value={
                          checkoutCart.customerServiceFeeTotalInCents / 100
                        }
                      />
                    </AppText>
                    <AppText style={{ fontSize: 14, color: '#666' }}>
                      {currency}
                    </AppText>
                  </AppFlexbox>
                </AppFlexbox>
              )}

              {checkoutCart.tieredPricingFeeInCents > 0 && (
                <AppFlexbox
                  style={{
                    justifyContent: 'space-between',
                    flexWrap: 'nowrap'
                  }}
                >
                  <AppText style={{ fontSize: 14, color: '#666' }}>
                    Platform Fee
                  </AppText>
                  <AppFlexbox style={{ gap: 10 }}>
                    <AppText style={{ fontSize: 14, color: '#666' }}>
                      <NumberFormat
                        decimalScale={2}
                        displayType="text"
                        fixedDecimalScale
                        prefix="$"
                        thousandSeparator
                        value={checkoutCart.tieredPricingFeeInCents / 100}
                      />
                    </AppText>
                    <AppText style={{ fontSize: 14, color: '#666' }}>
                      {currency}
                    </AppText>
                  </AppFlexbox>
                </AppFlexbox>
              )}
              <Divider style={{ margin: '10px 0px' }} />
              <AppFlexbox
                style={{
                  justifyContent: 'space-between',
                  flexWrap: 'nowrap'
                }}
              >
                <AppText size="lg" weight={700}>
                  Total
                </AppText>
                <AppFlexbox style={{ gap: 10 }}>
                  <AppText size="lg" weight={700}>
                    <NumberFormat
                      decimalScale={2}
                      displayType="text"
                      fixedDecimalScale
                      prefix="$"
                      thousandSeparator
                      value={checkoutCart.totalInCents / 100}
                    />
                  </AppText>
                  <AppText size="lg" weight={700}>
                    {currency}
                  </AppText>
                </AppFlexbox>
              </AppFlexbox>
            </AppStack>
          </AppStack>
          <AppStack
            style={{
              flex: rs([1, 1, 2], mqIndex),
              gap: 0,
              alignItems: rs(['start', 'start', 'end'], mqIndex),
              alignSelf: 'stretch',
              justifyContent: 'space-between',
              textAlign: rs(['start', 'start', 'end'], mqIndex)
            }}
          >
            <AppStack
              style={{
                gap: 0,
                alignSelf: 'stretch',
                flexDirection: rs(['row', 'row', 'column'], mqIndex),
                justifyContent: 'space-between'
              }}
            >
              <AppText
                style={{ fontSize: 18, whiteSpace: 'nowrap' }}
                weight={700}
              >
                Order # {regCart.pkRegCart}
              </AppText>
              <AppText style={{ fontSize: 18 }}>
                {isPendingPayment
                  ? 'PENDING'
                  : hasBeenRefunded
                  ? 'REFUNDED'
                  : 'COMPLETED'}
              </AppText>
            </AppStack>

            {regCart?.cart.purchasedAt && (
              <AppText style={{ fontSize: 18, textAlign: 'end' }}>
                Ordered on{' '}
                {dayjs(formatUtcDate(regCart?.cart.purchasedAt)).format(
                  'MMM D, YYYY'
                )}
              </AppText>
            )}
          </AppStack>
        </AppFlexbox>
      </AppCard>

      {showPaymentPlan && paymentPlans.length > 0 && (
        <AppCard
          style={{ border: withBorder ? 'solid 1px lightgrey' : 'none' }}
        >
          <AppStack style={{ padding: '0px 15px', gap: 16 }}>
            <AppText size="lg" weight={700}>
              Payment plan
            </AppText>

            {paymentPlans.map((payment) => (
              <AppStack key={payment.entityId} style={{ gap: 12 }}>
                <AppFlexbox
                  style={{
                    gap: 40,
                    flexWrap: 'wrap',
                    justifyContent: 'space-between'
                  }}
                >
                  <AppStack style={{ gap: 0 }}>
                    <AppText style={{ fontSize: 12, color: '#666' }}>
                      PLAN TOTAL
                    </AppText>
                    <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                      <NumberFormat
                        decimalScale={2}
                        displayType="text"
                        fixedDecimalScale
                        prefix="$"
                        thousandSeparator
                        value={payment.amountInCents / 100}
                      />
                    </AppText>
                  </AppStack>

                  <AppStack style={{ gap: 0 }}>
                    <AppText style={{ fontSize: 12, color: '#666' }}>
                      PAID
                    </AppText>
                    <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                      <NumberFormat
                        decimalScale={2}
                        displayType="text"
                        fixedDecimalScale
                        prefix="$"
                        thousandSeparator
                        value={payment.installmentsPaidInCents / 100}
                      />
                    </AppText>
                  </AppStack>

                  <AppStack style={{ gap: 0 }}>
                    <AppText style={{ fontSize: 12, color: '#666' }}>
                      SCHEDULED
                    </AppText>
                    <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                      <NumberFormat
                        decimalScale={2}
                        displayType="text"
                        fixedDecimalScale
                        prefix="$"
                        thousandSeparator
                        value={payment.installmentsScheduledInCents / 100}
                      />
                    </AppText>
                  </AppStack>
                </AppFlexbox>

                <AppStack style={{ overflow: 'auto', gap: 0 }}>
                  <Table withColumnBorders withRowBorders withTableBorder>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th
                          style={{
                            padding: '8px 16px',
                            backgroundColor: 'rgba(56, 56, 56, 0.1)'
                          }}
                        >
                          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                            Date
                          </AppText>
                        </Table.Th>
                        <Table.Th
                          style={{
                            padding: '8px 16px',
                            backgroundColor: 'rgba(56, 56, 56, 0.1)'
                          }}
                        >
                          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                            Amount
                          </AppText>
                        </Table.Th>
                        <Table.Th
                          style={{
                            padding: '8px 16px',
                            backgroundColor: 'rgba(56, 56, 56, 0.1)'
                          }}
                        >
                          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                            Status
                          </AppText>
                        </Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {[...payment.installments]
                        .sort((a, b) => a.sort - b.sort)
                        .map((installment) => {
                          const status =
                            WEBSITE_PAYMENT_STATUS_LIST.find(
                              (s) =>
                                s.value === installment.paymentStatus.toString()
                            ) ?? {};

                          return (
                            <Table.Tr key={installment.entityId}>
                              <Table.Td style={{ padding: '8px 16px' }}>
                                <AppText
                                  style={{ fontSize: 14, fontWeight: 500 }}
                                >
                                  {dayjs(
                                    formatLocalDate(installment.dueDate)
                                  ).format('MMM D, YYYY')}
                                </AppText>
                              </Table.Td>
                              <Table.Td style={{ padding: '8px 16px' }}>
                                <AppText style={{ fontSize: 14 }}>
                                  <NumberFormat
                                    decimalScale={2}
                                    displayType="text"
                                    fixedDecimalScale
                                    prefix="$"
                                    thousandSeparator
                                    value={
                                      (installment.chargeAmount -
                                        (installment.refundAmount ?? 0)) /
                                      100
                                    }
                                  />
                                </AppText>
                              </Table.Td>
                              <Table.Td style={{ padding: '8px 16px' }}>
                                <AppText
                                  style={{
                                    fontSize: 14,
                                    color: status?.color || '#000'
                                  }}
                                >
                                  {status?.label || 'Unknown'}
                                </AppText>
                              </Table.Td>
                            </Table.Tr>
                          );
                        })}
                    </Table.Tbody>
                  </Table>
                </AppStack>

                <AppFlexbox style={{ justifyContent: 'flex-end' }}>
                  <AppText
                    component={Link}
                    style={{
                      fontSize: 14,
                      textDecoration: 'none',
                      color: 'dodgerblue'
                    }}
                    to={`/payments/plans/${regCart.pkRegCart}/${payment.entityId}`}
                    weight={500}
                  >
                    Manage payment plan
                  </AppText>
                </AppFlexbox>
              </AppStack>
            ))}
          </AppStack>
        </AppCard>
      )}

      <AppStack
        style={{
          flex: 2,
          gap: rs([10, 10, 20], mqIndex),
          marginBottom: rs([10, 10, 0], mqIndex)
        }}
      >
        {regCart.cart.lineItems.map((i) => {
          const storeProductData = i.isBundleProduct
            ? groupedProductData.find(
                (p) => p.ecomOrderPackage?.pkEcomOrderPackage === i.entityId
              )
            : groupedProductData.find((p) => p.key === i.entityId);

          return i.lineItemType ===
            PAYMENT_PROVIDER_LINE_ITEM_TYPES.ecommerce.value ? (
            <AppCard
              key={`${i.entityId}-${i.serviceType}`}
              shadow="xs"
              style={{ padding: 16, borderRadius: isTabletOrSmaller ? 0 : 8 }}
              withBorder={withBorder}
            >
              <WsAccountOrderSummaryProductsGrouped
                baseStoreUrl={baseUrl}
                ecomOrderPackage={storeProductData.ecomOrderPackage}
                ecomOrderPackageLineItem={
                  storeProductData.ecomOrderPackageLineItem
                }
                filterListByCancelled
                padding={0}
                products={storeProductData.products}
              />
            </AppCard>
          ) : (
            <CheckoutRegCartLineItem
              key={`${i.entityId}-${i.serviceType}`}
              isOrderHistory
              lineItem={i}
              regCart={regCart}
              withBorder={withBorder}
            />
          );
        })}
      </AppStack>
    </>
  ) : (
    <>
      <AppCard style={{ border: withBorder ? 'solid 1px lightgrey' : 'none' }}>
        <AppFlexbox
          style={{
            padding: '0px 15px',
            alignItems: 'start',
            flexDirection: rs(
              ['column-reverse', 'column-reverse', 'row'],
              mqIndex
            )
          }}
        >
          <AppStack
            style={{
              flex: 1,
              gap: 10,
              alignSelf: 'stretch'
            }}
          >
            <Skeleton height={14} width={150} />
            <AppStack style={{ gap: 10 }}>
              <AppFlexbox
                style={{
                  justifyContent: 'space-between',
                  flexWrap: 'nowrap',
                  gap: 40
                }}
              >
                <Skeleton height={14} width={100} />
                <Skeleton height={14} width={100} />
              </AppFlexbox>
              <AppFlexbox
                style={{
                  justifyContent: 'space-between',
                  flexWrap: 'nowrap'
                }}
              >
                <Skeleton height={14} width={100} />
                <Skeleton height={14} width={100} />
              </AppFlexbox>
              <Divider style={{ margin: '10px 0px' }} />
              <AppFlexbox
                style={{
                  justifyContent: 'space-between',
                  flexWrap: 'nowrap'
                }}
              >
                <Skeleton height={14} width={100} />
                <Skeleton height={14} width={100} />
              </AppFlexbox>
            </AppStack>
          </AppStack>
          <AppStack
            style={{
              flex: rs([1, 1, 2], mqIndex),
              gap: 0,
              alignItems: 'end',
              alignSelf: 'stretch',
              justifyContent: 'space-between',
              flexDirection: rs(['row', 'row', 'column'], mqIndex)
            }}
          >
            <Skeleton height={14} width={100} />
            <Skeleton height={14} width={250} />
          </AppStack>
        </AppFlexbox>
      </AppCard>

      <AppStack
        style={{
          flex: 2,
          gap: rs([10, 10, 20], mqIndex),
          width: rs(['100%', '100%', 'unset'], mqIndex),
          marginBottom: rs([10, 10, 0], mqIndex)
        }}
      >
        <WsCheckoutLineItem isOrderHistory />
        <WsCheckoutLineItem isOrderHistory />
      </AppStack>
    </>
  );
};

OrderDetailsInfo.propTypes = {
  hideTitle: PropTypes.bool,
  isLoading: PropTypes.bool,
  regCart: PropTypes.object,
  showPaymentPlan: PropTypes.bool,
  withBorder: PropTypes.bool
};

export default OrderDetailsInfo;
